// Shown when GET /api/admin/me refuses the boot. The admin surface signs in with
// its OWN Google OAuth: `/api/auth/google/begin` is served same-origin on the
// admin host and lands the operator back here after the callback. Any
// fiverr.com / f-labs.io Google account is granted employee access; area grants
// are managed separately.
//
// THREE refusals, and they need different words AND different buttons (the
// backend names which one it is in the 403 detail's `error`, `web/deps/identity.py`):
//   signed-out     — 401, no identity at all.
//   wrong-identity — 403 `admin_signin_required`: an identity arrived, it just
//                    isn't an admin one. In dev/preprod that is the ORDINARY
//                    case: those envs share one cookie domain between the buyer
//                    app and the admin host, so an operator who used the client
//                    SPA carries a `fiverr_authority` cookie here, and it is not
//                    an employee. Google sign-in fixes it.
//   not-employee   — 403 `not_employee`: an attested Google account off the
//                    allowed domains. A different account is the only way in.
// Every state offers Sign in with Google AND Sign out. The panel used to hide
// sign-in on any 403 and offer Retry alone, which turned that dev/preprod cookie
// into a locked door: the reload Retry runs sends the same cookie and refuses
// the same way, forever.
//
// This is the first screen anyone sees, so it carries the full Mira treatment:
// centered card on a quiet accent aurora, serif wordmark, one-line serif promise.
import { Button } from "@recruiter/shared";

import { ADMIN_API_BASE } from "../config";
import { clearSignedOut } from "../lib/identity";
import { rememberReturnTo } from "../lib/return-to";
import type { SignInReason } from "../lib/sign-in-reason";
import { signOutAdmin } from "../lib/sign-out";

function signInWithGoogle() {
  // Explicit "get me back in": clear the signed-out flag first so a stub-mode
  // reload re-enables the stub header; in cloud the OAuth flow sets the cookie.
  clearSignedOut();
  // Remember WHERE we are before leaving for Google. This panel replaces
  // whatever the operator opened (typically a deep link from a Slack alert) and
  // the callback can only redirect to the admin root, because a client-side
  // route never reached the backend. `main.tsx` puts the path back on the way
  // in (lib/return-to.ts).
  rememberReturnTo(window.location);
  window.location.href = `${ADMIN_API_BASE}/api/auth/google/begin`;
}

function retry() {
  clearSignedOut();
  window.location.reload();
}

const BODY: Record<SignInReason, string> = {
  "signed-out": "Sign in with your Fiverr Google account to open the console.",
  "wrong-identity":
    "You're signed in, but not as an admin. The console has its own Google sign-in, separate from your Mira client session. Sign in with your fiverr.com or f-labs.io Google account.",
  "not-employee":
    "You're signed in with Google, but this account isn't an authorized employee. Admin access is limited to fiverr.com and f-labs.io Google accounts, so sign in with one of those.",
};

export function SignInPanel({ reason = "signed-out" }: { reason?: SignInReason }) {
  return (
    <div className="ad-signin">
      <div className="ad-signin__card">
        <div className="ad-signin__wordmark">
          <span className="ad-brand__mark" aria-hidden="true">
            M
          </span>
          <h1 className="ad-signin__title">
            Mira
            <span className="ad-signin__title-sub">Admin console</span>
          </h1>
        </div>
        <p className="ad-signin__promise">The desk behind the hiring assistant.</p>
        <p className="ad-signin__body">{BODY[reason]}</p>
        <div className="ad-signin__actions">
          <Button variant="solid" tone="accent" onClick={signInWithGoogle}>
            {reason === "not-employee" ? "Sign in with another account" : "Sign in with Google"}
          </Button>
          <Button variant="outline" tone="neutral" onClick={() => signOutAdmin()}>
            Sign out
          </Button>
          <Button variant="ghost" tone="neutral" onClick={retry}>
            Retry
          </Button>
        </div>
        <p className="ad-signin__foot">Access is limited to employee accounts.</p>
      </div>
    </div>
  );
}
